<script setup lang="ts">
import { ref, reactive } from 'vue'
import { z } from 'zod'
import type { FormSubmitEvent } from '@nuxt/ui'

const { isAuthenticated, user } = useAuth()

const { $pb } = useNuxtApp()

const toast = useToast()

definePageMeta({
  layout: 'public'
})

const state = reactive({
  email: '',
  password: ''
})

const schema = z.object({
  email: z.string().email(),
  password: z.string().min(8)
})

type Schema = z.output<typeof schema>



const loading = ref(false)

interface State {
  email: string;
  password: string;
}

onMounted(() => {
  if (isAuthenticated) {
    navigateTo($pb.authStore.record?.userType.includes('candidate') ? '/profile' : '/admin/startups')
  }
})

const fields = [{
  name: 'email',
  type: 'text' as const,
  label: 'Email',
  placeholder: 'Enter your email',
  required: true
}, {
  name: 'password',
  label: 'Password',
  type: 'password' as const,
  placeholder: 'Enter your password',
  required: true
}, {
  name: 'remember',
  label: 'Remember me',
  type: 'checkbox' as const
}]

const provider = ref('')

const providers = [{
  label: 'Continue with Google',
  icon: 'i-logos-google-icon',
  color: 'neutral' as const,
  onClick: async () => {
    try {
      loading.value = true
      provider.value = 'Google'

      const state = btoa(JSON.stringify({
        referrer: 'sign-in',
        timestamp: Date.now()
      }))

      await $pb.collection('users').authWithOAuth2({
        provider: 'google',
        query: {
          state: state
        }
      })

      if ($pb.authStore.token) {
        navigateTo($pb.authStore.record?.userType.includes('candidate') ? '/' : '/admin/startups')
      }
    } catch (error: any) {
      if (error.message === 'User not found for sign-in.') {
        toast.add({
          id: 'error',
          title: 'Authentication Failed',
          description: 'It looks like you might not have an account yet.',
          icon: 'i-heroicons-exclamation-triangle',
          color: 'error',
          duration: 10000,
          actions: [
            {
              label: 'Sign up as Candidate',
              onClick: () => {
                toast.remove('error')
                navigateTo('/sign-up')
              }
            },
            {
              label: 'Sign up as Startup',
              onClick: () => {
                toast.remove('error')
                navigateTo('/startup-sign-up')
              }
            }
          ]
        })
        return
      }
      toast.add({
        title: 'Authentication Failed',
        description: error.message,
        icon: 'i-heroicons-exclamation-triangle',
        color: 'error',
        duration: 5000
      })
    } finally {
      loading.value = false
    }
  }
}]

const onSubmit = async (payload: FormSubmitEvent<Schema>) => {
  try {
    loading.value = true
    toast.add({
      title: 'Authenticating',
      description: 'Attempting to log you in',
      icon: 'i-heroicons-lock-closed',
      color: 'info',
      duration: 2000
    })

    await $pb.collection('users').authWithPassword(payload.data.email, payload.data.password)

    if ($pb.authStore.token) {
      navigateTo($pb.authStore.record?.userType.includes('candidate') ? '/profile' : '/admin/startups')
    }


    toast.add({
      title: 'Authenticating',
      description: 'You have been succesffully logged in',
      icon: 'i-heroicons-tick-circle',
      color: 'success',
      duration: 2000
    })

  } catch (error: any) {

    toast.add({
      title: 'Authentication Failed',
      description: error.message,
      icon: 'i-heroicons-exclamation-triangle',
      color: 'error',
      duration: 5000
    })
    // console.log(error)
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="relative">
    <div
      v-if="loading && provider !== ''"
      class="absolute top-0 left-0 w-full h-full bg-slate-600/40 z-10 flex justify-center items-center text-4xl"
    >
      Waiting for you to authorize with {{ provider }}...
    </div>
    <div class="flex flex-col items-center justify-center min-h-[calc(100vh-100px)]">
      <UCard>
        <UAuthForm
          :fields="fields"
          :schema="schema"
          :providers="providers"
          title="Welcome back"
          @submit="onSubmit"
        >
          <template #title>
            <div class="flex flex-col items-center justify-center brunson text-3xl tracking-wider">
              Welcome back
            </div>
          </template>
          <template #description>
            Don't have an account?
            <ULink
              to="/sign-up"
              class="text-(--ui-primary) font-medium"
            >
              Sign up
            </ULink>.
          </template>

          <template #password-hint>
            <ULink
              to="/forgot-password"
              class="text-(--ui-primary) font-medium"
            >
              Forgot password?
            </ULink>
          </template>

          <template #footer>
            By signing in, you agree to our
            <ULink
              to="/legal/terms-of-service"
              class="text-(--ui-primary) font-medium"
            >
              Terms of Service
            </ULink>.
          </template>
        </UAuthForm>
      </UCard>
    </div>
  </div>
</template>
